// /disputes/policy — the dispute policy editor (SM-291 / SM-295 / SM-324).
//
// Policy resolves MARKET → EVENT → CATEGORY → GLOBAL, and each FIELD
// falls through independently. That per-field inheritance is the thing
// the UI has to make legible: an operator who overrides the stake for
// one category must be able to see at a glance that the window is still
// coming from GLOBAL, or they will assume they reset it.
//
// Hence a row shows an explicit "inherit" for every unset field rather
// than rendering the effective value — showing the inherited number would
// make the override look set, and clearing it would then look like a
// no-op.
//
// SM-324: the page used to be one flat table of every stored row ordered
// by scope NAME, so GLOBAL — the floor everything falls back to — landed
// between EVENT and MARKET, and above it sat a hardcoded "defaults" card
// that could disagree with the live GLOBAL row it was describing. A flat
// list of heterogeneous rows reads as a history log; "what is configured
// right now" was the one question it could not answer. The page is now
// the hierarchy the copy describes: one section per scope level, general
// → specific, the global policy first and read from the live GLOBAL row.
// Above that sits the question operators actually arrive with — what
// applies to THIS market — answered by the server's own resolution, each
// value naming the row it came from.
//
// SM-319 finished that removal. A copy of eventsv1.DefaultDispute* still
// lived here and was rendered in two places: as a stand-in card whenever
// no GLOBAL row was stored, and as the greyed value of any field the
// GLOBAL row left unset. Both were numbers this page asserted on the
// service's behalf, and neither had any way of noticing when the service
// changed them — the same disagreement the old defaults card could have
// with the row beneath it, moved one level down. The constants are gone:
// a field nothing in the chain supplies now reads "Not set", and a
// missing GLOBAL row is an empty state rather than four invented values.
// The one number-bearing answer left on the page is the lookup at the
// top, which is the server's own resolution.
//
// SM-551: ONE policy per catalog node, and the stake CURRENCY is one of
// its fields. For one sprint (SM-512) the row was keyed by book and by
// currency, this page was read one currency at a time, and the currency
// a market was charged in was DERIVED from the book it traded — which
// quoted a GAME holder of a dual-book market a GOLD stake for a position
// bought with RED, and let a RED row be stored that nothing ever read. A
// market resolves to one result and has one dispute slot, so its dispute
// terms are one decision shared by every book it trades. The currency is
// now chosen at a scope and inherited by the scopes below, exactly like
// the stake, with its own "from …" beside it — so the picker at the top
// is gone, each scope holds one row, and a row's stake is always shown in
// the currency it would actually be charged in.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { PolicyEmpty, PolicySection } from "@/components/ui/PolicySection";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount, CurrencyMonogram } from "@/components/ui/CurrencyAmount";
import { campaignCurrency, type CurrencyCode } from "@/lib/currency";
import { useMyPerms } from "@/hooks/useMyPerms";
import {
  formatPolicySeconds,
  policySourceLabel,
  useDisputePolicies,
  useEffectiveDisputePolicy,
  type DisputePolicyItem,
  type DisputePolicyScope,
  type DisputePolicyView,
} from "@/lib/dispute-queries";
import {
  fetchCategoryTree,
  fetchRule,
  fetchTopic,
  keys,
  type CategoryTreeNode,
} from "@/lib/queries";
import type { MarketItem } from "@/lib/types";
import { MarketPickField } from "@/components/fees/MarketPickField";
import {
  DisputePolicyModal,
  type DisputePolicyTarget,
} from "@/components/disputes/DisputePolicyModal";
import { fmtRelative } from "@/lib/format";
import { Perm } from "@/lib/perms";

export const Route = createFileRoute("/_app/disputes/policy")({
  component: DisputePolicyPage,
});

const SECTION: Record<
  Exclude<DisputePolicyScope, "GLOBAL">,
  { heading: string; caption: string; empty: string }
> = {
  CATEGORY: {
    heading: "Category overrides",
    caption:
      "Applies to every event and market in the category, for the fields it sets.",
    empty: "No category overrides. Every category falls through to the global policy.",
  },
  EVENT: {
    heading: "Event overrides",
    caption:
      "Applies to the event and its markets. An event-level resolve reads these and never a child market's own row.",
    empty: "No event overrides.",
  },
  MARKET: {
    heading: "Market overrides",
    caption: "The most specific level. Applies to exactly one market, whichever books it trades.",
    empty: "No market overrides.",
  },
};

function DisputePolicyPage() {
  const perms = useMyPerms();
  const canEdit = perms.has(Perm.DisputePolicy);
  const q = useDisputePolicies();
  const items = useMemo(() => q.data?.items ?? [], [q.data]);
  const [target, setTarget] = useState<DisputePolicyTarget | null>(null);

  const categoryTreeQ = useQuery({
    queryKey: [...keys.catalog, "category-tree", "dispute-scope"] as const,
    queryFn: () => fetchCategoryTree({ includeArchived: true }),
  });
  const categoryNames = useMemo(
    () => categoryNameMap(categoryTreeQ.data?.tree ?? []),
    [categoryTreeQ.data],
  );
  const eventIDs = useMemo(() => scopeIDsOf(items, "EVENT"), [items]);
  const eventQueries = useQueries({
    queries: eventIDs.map((id) => ({
      queryKey: keys.topicDetail(id),
      queryFn: () => fetchTopic(id),
    })),
  });
  const events = useMemo(
    () =>
      new Map(
        eventQueries
          .map((query) => query.data)
          .filter((event): event is NonNullable<typeof event> => Boolean(event))
          .map((event) => [event.event_id, event]),
      ),
    [eventQueries],
  );
  const marketIDs = useMemo(() => scopeIDsOf(items, "MARKET"), [items]);
  const marketQueries = useQueries({
    queries: marketIDs.map((id) => ({
      queryKey: keys.ruleDetail(id),
      queryFn: () => fetchRule(id),
    })),
  });
  const markets = useMemo(
    () =>
      new Map(
        marketQueries
          .map((query) => query.data)
          .filter((market): market is NonNullable<typeof market> => Boolean(market))
          .map((market) => [market.market_id, market]),
      ),
    [marketQueries],
  );
  const resolvedPolicies = useMemo(
    () => resolvePolicies(items, events, markets),
    [items, events, markets],
  );

  // One bucket per scope level, so a level with no overrides still gets a
  // section saying so — an absent section would read as "not a thing you
  // can set here".
  const byScope = useMemo(() => {
    const out = new Map<DisputePolicyScope, DisputePolicyItem[]>([
      ["GLOBAL", []],
      ["CATEGORY", []],
      ["EVENT", []],
      ["MARKET", []],
    ]);
    for (const p of items) out.get(p.scope)?.push(p);
    return out;
  }, [items]);
  const global = byScope.get("GLOBAL")?.[0] ?? null;

  if (!perms.has(Perm.DisputeView)) {
    return <EmptyState title="No access" description="You need DISPUTE_VIEW." />;
  }

  const nameOf = (p: DisputePolicyItem) =>
    policyScopeName(p, categoryNames, events, markets);

  const row = (p: DisputePolicyItem) => (
    <PolicyRow
      key={p.policy_id}
      p={p}
      resolved={resolvedPolicies.get(p.policy_id) ?? {}}
      scopeName={nameOf(p)}
      onEdit={
        canEdit
          ? () =>
              setTarget({
                mode: "edit",
                policy: p,
                scopeName: nameOf(p),
                inheritedCurrency: resolvedPolicies.get(p.policy_id)?.currency,
              })
          : undefined
      }
    />
  );

  // What a NEW override would inherit as its currency before it chooses
  // one. The exact node is not known until the operator picks it inside
  // the dialog, so this is the floor every chain shares — the GLOBAL
  // row's choice, or the built-in GOLD when it makes none.
  const createInherits = global?.currency;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/disputes" className="text-2xs text-muted hover:text-fg">
          ← Disputes
        </Link>
        <h1 className="mt-1 font-display text-2xl tracking-tight">Dispute policy</h1>
        <p className="max-w-[75ch] text-sm text-muted">
          Stake currency, stake, window, and review deadline. Resolved per field,
          most specific scope first:{" "}
          <span className="text-fg">MARKET → EVENT → CATEGORY → GLOBAL</span>. An
          unset field inherits from the next scope out — overriding one field
          never resets the others.
        </p>
        <p className="max-w-[75ch] text-sm text-muted">
          One policy per market, whichever books it trades: a Cash holder and a
          Game holder of the same market are charged the same stake in the same
          currency.
        </p>
      </div>

      <EffectivePolicyLookup />

      {q.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-20 rounded-lg" />
        </div>
      )}
      {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}

      {!q.isLoading && !q.isError && (
        <div className="space-y-6">
          <PolicySection
            heading="Global policy"
            caption="The active platform-wide policy — the floor every other row falls back to, field by field, including the stake currency."
          >
            {global ? (
              row(global)
            ) : (
              <NoGlobalPolicy
                onCreate={
                  canEdit ? () => setTarget({ mode: "create", scope: "GLOBAL" }) : undefined
                }
              />
            )}
          </PolicySection>

          {(["CATEGORY", "EVENT", "MARKET"] as const).map((scope) => {
            const rows = byScope.get(scope) ?? [];
            return (
              <PolicySection
                key={scope}
                heading={SECTION[scope].heading}
                caption={SECTION[scope].caption}
                count={rows.length}
                action={
                  canEdit ? (
                    <NewOverrideButton
                      onClick={() =>
                        setTarget({ mode: "create", scope, inheritedCurrency: createInherits })
                      }
                    />
                  ) : null
                }
              >
                {rows.length === 0 ? (
                  <PolicyEmpty text={SECTION[scope].empty} />
                ) : (
                  rows.map(row)
                )}
              </PolicySection>
            );
          })}
        </div>
      )}

      <p className="max-w-[75ch] text-2xs text-muted">
        Enabling disputes at any scope has no effect unless the fleet also runs
        with DISPUTES_ENABLED. Both switches must be on — the lookup above
        reports the fleet switch for the market you pick.
      </p>

      <DisputePolicyModal target={target} onClose={() => setTarget(null)} />
    </div>
  );
}

/**
 * "What applies to this market" — the question the stored-row list cannot
 * answer, because the answer is assembled from up to four rows plus the
 * built-in defaults plus the deployment flag.
 *
 * Deliberately a read of the server's own resolution (the endpoint the
 * publish flow already uses) rather than a client-side replay of the
 * chain: a second implementation of the fallthrough would eventually
 * disagree with the one that actually charges the stake.
 *
 * ONE answer per market (SM-551). It used to be one per enabled book,
 * each in a currency derived from that book; a market now has one set of
 * dispute terms whichever books it trades, and the currency is one of
 * them — shown with the scope that chose it, like every other value.
 */
function EffectivePolicyLookup() {
  const [market, setMarket] = useState<MarketItem | null>(null);

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>What applies to a market</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        <MarketPickField label="Market" value={market} onChange={setMarket} />

        {!market && (
          <p className="text-2xs text-muted">
            Pick a market to see the terms it resolves to right now — the same
            terms for every holder, whichever book they trade — and which
            scope every value comes from.
          </p>
        )}

        {market && <MarketTerms marketID={market.market_id} />}
      </CardBody>
    </Card>
  );
}

/** A market's single set of resolved dispute terms. */
function MarketTerms({ marketID }: { marketID: string }) {
  const q = useEffectiveDisputePolicy({ scope: "MARKET", id: marketID });
  const p = q.data?.policy;
  const currency = campaignCurrency(p?.stake_currency);

  return (
    <div className="space-y-3">
      {q.isLoading && <Skeleton className="h-16 rounded-lg" />}
      {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}

      {p && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Resolved
              label="Disputes"
              source={p.source_scope}
              value={
                <span className={p.enabled ? "text-success" : "text-muted"}>
                  {p.enabled ? "Enabled" : "Disabled"}
                </span>
              }
            />
            <Resolved
              label="Stake currency"
              source={p.currency_source}
              value={<CurrencyLabel code={currency} />}
            />
            <Resolved
              label="Stake"
              // No source when nothing supplied the value: naming one
              // would point at a row that does not exist.
              source={p.stake_configured === false ? undefined : p.stake_source}
              value={<ResolvedStake policy={p} currency={currency} />}
            />
            <Resolved
              label="Dispute window"
              source={p.window_source}
              value={formatPolicySeconds(p.window_seconds)}
            />
            <Resolved
              label="Overdue after"
              source={p.overdue_source}
              value={formatPolicySeconds(p.overdue_seconds)}
            />
          </div>

          {p.enabled && p.stake_configured === false && (
            <p className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-2xs text-t-2">
              {`Disputes are switched on for this market but nothing prices them in ${currency}, the stake currency chosen by ${policySourceLabel(p.currency_source)}. No dispute window opens and publishing is refused until a ${currency} stake is set at any scope above, or on this market.`}
            </p>
          )}
          {!q.data?.feature_enabled && (
            <p className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-2xs text-t-2">
              This fleet runs with DISPUTES_ENABLED off, so no market is
              disputable whatever the policy says. The values above are what
              would apply once the flag is on.
            </p>
          )}
          {!q.data?.override && (
            <p className="text-2xs text-muted">
              This market has no override of its own — every value above is
              inherited.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The stake figure, or the fact that there isn't one.
 *
 * "0" is what an unpriced currency resolves to, and rendering it as
 * money would say disputes here are free — the one misreading with a
 * user's wallet attached.
 */
function ResolvedStake({
  policy,
  currency,
}: {
  policy: DisputePolicyView;
  currency: CurrencyCode;
}) {
  if (policy.stake_configured === false) {
    return <span className="text-warning">Not configured</span>;
  }
  return <CurrencyAmount currency={currency} value={policy.stake_amount} />;
}

/**
 * A currency named by mark and code. The three currencies share a wire
 * scale, so the mark is the fastest way to tell, without reading, which
 * money a stake is in.
 */
function CurrencyLabel({ code }: { code: CurrencyCode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <CurrencyMonogram currency={code} />
      <span>{code}</span>
    </span>
  );
}

/** One resolved field, labelled with the row it came from. */
function Resolved({
  label,
  value,
  source,
}: {
  label: string;
  value: React.ReactNode;
  source?: DisputePolicyScope | "";
}) {
  return (
    <div>
      <p className="text-2xs uppercase tracking-wide text-muted">{label}</p>
      <p className="text-sm tabular-nums text-fg">{value}</p>
      <p className="text-2xs text-t-4">from {policySourceLabel(source)}</p>
    </div>
  );
}

/**
 * What a row's unset fields inherit, assembled from the rows ABOVE it.
 *
 * Every field is optional and stays absent when no stored row supplies
 * it: the value that applies then is the service's built-in default, and
 * this page no longer keeps a copy of those numbers to render in its
 * place (SM-319). A GLOBAL row has nothing above it, so its resolution
 * is empty by construction and an unset field there always reads
 * "Not set".
 *
 * `currency` is the one field with a built-in default this page DOES
 * name — GOLD — because it is not a number that can drift: it is the
 * rail every pre-SM-551 stake was collected on, and a stake figure with
 * no currency beside it would be unreadable (SM-551).
 */
interface ResolvedPolicy {
  currency?: string;
  enabled?: string;
  stakeAmount?: string;
  windowSeconds?: number;
  overdueSeconds?: number;
}

function PolicyRow({
  p,
  resolved,
  scopeName,
  onEdit,
}: {
  p: DisputePolicyItem;
  resolved: ResolvedPolicy;
  scopeName?: string;
  onEdit?: () => void;
}) {
  const isGlobal = p.scope === "GLOBAL";
  // The currency this row's stake is charged in: its own choice, else
  // the one it inherits, else the built-in GOLD. Every figure on the
  // row wears THIS mark — never a default of the page's own.
  const stakeCurrency = campaignCurrency(p.currency || resolved.currency);
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-border/60 px-4 py-3 last:border-0">
      <div className="min-w-0 flex-1 basis-48">
        <p className="flex items-center gap-2 text-sm font-medium text-fg">
          <span className="truncate">
            {isGlobal
              ? "Global policy"
              : (scopeName ?? `Unknown ${p.scope.toLowerCase()}`)}
          </span>
          {isGlobal && (
            <Badge
              tone="accent"
              title="The stored GLOBAL row — the policy in effect platform-wide."
            >
              Active
            </Badge>
          )}
        </p>
        <p className="truncate text-2xs text-muted">
          {p.updated_at_ms
            ? `Changed ${fmtRelative(new Date(p.updated_at_ms).toISOString())}${
                p.updated_by ? ` by ${p.updated_by}` : ""
              }`
            : (p.scope_id ?? "")}
        </p>
      </div>

      <Value label="Stake currency">
        {p.currency ? (
          <CurrencyLabel code={campaignCurrency(p.currency)} />
        ) : resolved.currency ? (
          <Inherited>
            <CurrencyLabel code={campaignCurrency(resolved.currency)} />
          </Inherited>
        ) : (
          <NotSet />
        )}
      </Value>
      <Value label="Disputes">
        {p.enabled ? (
          <span className={p.enabled === "true" ? "text-success" : "text-muted"}>
            {p.enabled === "true" ? "Enabled" : "Disabled"}
          </span>
        ) : resolved.enabled ? (
          <Inherited>
            {resolved.enabled === "true" ? "Enabled" : "Disabled"}
          </Inherited>
        ) : (
          <NotSet />
        )}
      </Value>
      <Value label="Stake">
        {p.stake_amount ? (
          <CurrencyAmount currency={stakeCurrency} value={p.stake_amount} />
        ) : resolved.stakeAmount ? (
          <Inherited>
            <CurrencyAmount currency={stakeCurrency} value={resolved.stakeAmount} />
          </Inherited>
        ) : (
          <NotSet />
        )}
      </Value>
      <Value label="Window">
        {p.window_seconds ? (
          formatPolicySeconds(p.window_seconds)
        ) : resolved.windowSeconds ? (
          <Inherited>{formatPolicySeconds(resolved.windowSeconds)}</Inherited>
        ) : (
          <NotSet />
        )}
      </Value>
      <Value label="Overdue after">
        {p.overdue_seconds ? (
          formatPolicySeconds(p.overdue_seconds)
        ) : resolved.overdueSeconds ? (
          <Inherited>{formatPolicySeconds(resolved.overdueSeconds)}</Inherited>
        ) : (
          <NotSet />
        )}
      </Value>

      {onEdit && <EditButton onClick={onEdit} label={`Edit ${p.scope} override`} />}
    </li>
  );
}

/**
 * The empty state for a GLOBAL row that does not exist — the migration
 * seeds one, so this is the "every global field was cleared" case.
 *
 * It says what is true (nothing is configured platform-wide) and stops
 * there. The predecessor filled the row with this page's own copy of the
 * service defaults, which read as a stored policy an operator could go
 * and edit, and would keep reading that way after the service changed
 * them (SM-319). The numbers that do apply are one lookup away, from the
 * server, at the top of the page. The one default named is the currency,
 * which is not a number and cannot drift — see ResolvedPolicy.
 */
function NoGlobalPolicy({ onCreate }: { onCreate?: () => void }) {
  return (
    <li className="space-y-2 px-4 py-6 text-center">
      <p className="text-sm font-medium text-fg">No active global policy</p>
      <p className="mx-auto max-w-[60ch] text-2xs leading-relaxed text-muted">
        Nothing is set platform-wide, so every field an override leaves unset
        falls through to the service’s built-in default, which stakes disputes
        in GOLD. Use the lookup above to see what a given market resolves to
        right now.
      </p>
      {onCreate && (
        <button
          type="button"
          onClick={onCreate}
          className="rounded-md border border-line-1 bg-bg-2 px-2.5 py-1 text-2xs font-medium text-t-2 hover:border-line-3 hover:text-fg"
        >
          Set the global policy
        </button>
      )}
    </li>
  );
}

function Value({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="basis-28">
      <p className="text-2xs uppercase tracking-wide text-t-4">{label}</p>
      <p className="text-sm tabular-nums">{children}</p>
    </div>
  );
}

/**
 * Explicit "inherit" marker — see the file header for why. Only rendered
 * when a row ABOVE this one actually supplies the value; the marker
 * names a row an operator can go and edit, so it must never be attached
 * to a number this page made up.
 */
function Inherited({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-t-2">{children}</span>
      <span className="text-2xs italic text-t-4">(inherit)</span>
    </span>
  );
}

/**
 * A field no row in the chain supplies. The service's built-in default
 * applies, and that number is deliberately not repeated here — see the
 * file header.
 */
function NotSet() {
  return (
    <span
      className="text-t-4"
      title="Not set here or at any scope above — the service's built-in default applies."
    >
      Not set
    </span>
  );
}

function EditButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="rounded-md border border-line-1 bg-bg-2 px-2.5 py-1 text-2xs font-medium text-t-2 hover:border-line-3 hover:text-fg"
    >
      Edit
    </button>
  );
}

function NewOverrideButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-line-1 bg-bg-2 px-2.5 py-1 text-2xs font-medium text-t-2 hover:border-line-3 hover:text-fg"
    >
      + New override
    </button>
  );
}

function scopeIDsOf(items: DisputePolicyItem[], scope: DisputePolicyScope): string[] {
  return [
    ...new Set(
      items
        .filter((p) => p.scope === scope)
        .map((p) => p.scope_id)
        .filter(Boolean),
    ),
  ] as string[];
}

function categoryNameMap(nodes: CategoryTreeNode[]): Map<string, string> {
  const names = new Map<string, string>();
  const walk = (level: CategoryTreeNode[]) => {
    for (const category of level) {
      names.set(category.category_id, category.name);
      if (category.children?.length) walk(category.children);
    }
  };
  walk(nodes);
  return names;
}

function policyScopeName(
  policy: DisputePolicyItem,
  categories: Map<string, string>,
  events: Map<string, Awaited<ReturnType<typeof fetchTopic>>>,
  markets: Map<string, Awaited<ReturnType<typeof fetchRule>>>,
): string | undefined {
  if (policy.scope_name || !policy.scope_id) return policy.scope_name;
  if (policy.scope === "CATEGORY") return categories.get(policy.scope_id);
  if (policy.scope === "EVENT") return events.get(policy.scope_id)?.name;
  if (policy.scope === "MARKET") return markets.get(policy.scope_id)?.name;
  return undefined;
}

/**
 * What each row inherits from the rows ABOVE it, field by field — the
 * same walk the service performs, including the currency (SM-551). One
 * row per node, so a scope's chain entry is exactly one row; the
 * per-book merge of SM-489 is gone with the per-book rows.
 */
function resolvePolicies(
  items: DisputePolicyItem[],
  events: Map<string, Awaited<ReturnType<typeof fetchTopic>>>,
  markets: Map<string, Awaited<ReturnType<typeof fetchRule>>>,
): Map<string, ResolvedPolicy> {
  const policies = new Map(
    items.map((policy) => [`${policy.scope}:${policy.scope_id ?? ""}`, policy]),
  );
  const resolved = new Map<string, ResolvedPolicy>();

  for (const policy of items) {
    const at = (scope: string, id: string): DisputePolicyItem[] => {
      const row = policies.get(`${scope}:${id}`);
      return row ? [row] : [];
    };

    const chain: DisputePolicyItem[] = [...at("GLOBAL", "")];

    if (policy.scope === "CATEGORY" && policy.scope_id) {
      // Nothing between GLOBAL and a category.
    } else if (policy.scope === "EVENT" && policy.scope_id) {
      const event = events.get(policy.scope_id);
      if (event) chain.push(...at("CATEGORY", event.category_id));
    } else if (policy.scope === "MARKET" && policy.scope_id) {
      const market = markets.get(policy.scope_id);
      if (market) {
        chain.push(...at("CATEGORY", market.category_id));
        if (market.event_id) chain.push(...at("EVENT", market.event_id));
      }
    }

    // The row's OWN scope is excluded on purpose: this is what the row
    // would inherit for a field it leaves blank, not what it resolves to.
    const value: ResolvedPolicy = {};
    for (const level of chain) {
      if (level.currency) value.currency = level.currency;
      if (level.enabled) value.enabled = level.enabled;
      if (level.stake_amount) value.stakeAmount = level.stake_amount;
      if (level.window_seconds) value.windowSeconds = level.window_seconds;
      if (level.overdue_seconds) value.overdueSeconds = level.overdue_seconds;
    }
    resolved.set(policy.policy_id, value);
  }

  return resolved;
}
